import * as CategoriesActions from 'libs/gb-front/category/data-access/src/lib/+state/categories.actions'
import * as ItemActions from './items.actions'
import * as FavouriteItemActions from 'libs/gb-front/item/data-access-favourite-items/src/lib/+state/favourite-items.actions'
import * as SelectedItemActions from 'libs/gb-front/selected-item/data-access/src/lib/+state/selected-item.actions'

import { Injectable } from '@angular/core'
import { NavigationExtras, Router } from '@angular/router'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Action } from '@ngrx/store'
import { AuctionFacade } from 'libs/gb-front/auction/data-access/src'
import { AuthFacade } from 'libs/gb-front/auth/data-access/src/lib/+state/auth.facade'
import { GbPopperService } from 'libs/gb-front/item/feature/src/lib/gb-popper/gb-popper.service'
import { ApiUtils } from 'libs/shared/utils/helpers/src/lib/api-utils'
import { LoaderActionTypeEnum } from 'libs/shared/utils/loader/src'
import { GbLoaderService } from 'libs/shared/utils/loader/src/lib/services/gb-loader.service'
import { of } from 'rxjs'
import { catchError, distinctUntilChanged, filter, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators'
import { ItemApiService } from './items-api.service'
import { ItemFacade } from './items.facade'
import { ItemsResponse } from './items.models'
import { GbNotificationService } from 'libs/shared/utils/notification/src/lib/service/gb-notification.service'
import { SelectedProfileFacade } from 'libs/gb-front/selected-profile/data-access/src/lib/+state/selected-profile.facade'
import { SelectedItemFacade } from 'libs/gb-front/selected-item/data-access/src/lib/+state/selected-item.facade'
import { FavouriteItemsFacade } from 'libs/gb-front/item/data-access-favourite-items/src/lib/+state/favourite-items.facade'
import { NotFoundConstants } from 'libs/gb-front/not-found/feature/src/lib/gb-front-not-found-feature.module'
import { GbItemsObserverService } from 'libs/shared/ui/src/lib/gb-items-observer/gb-items-observer.service'
import { ObjectUtils } from 'libs/shared/utils/helpers/src/lib/object-utils'

@Injectable()
export class ItemsEffects {
  loadItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ItemActions.loadItems),
      withLatestFrom(this.auctionFacade.auctionUrlName$, this.itemFacade.items$),
      tap(() => this.loaderService.setLoading(LoaderActionTypeEnum.LoadItems)),
      switchMap(([, urlName, items]) => {
        let offset = 0
        if (items) {
          offset = items.length - offset
        }
        return this.itemService.getItems(urlName, offset).pipe(
          mergeMap((response: ItemsResponse) => [
            ItemActions.loadItemsSuccess({
              items: response.results,
            }),
            ItemActions.updateCount({
              count: response.count,
            }),
            ItemActions.updateOrderHash({
              order: response.order_hash,
            }),
          ]),
          catchError(async (error) => ItemActions.loadItemsFailure({ error })),
        )
      }),
    ),
  )

  setLoaderComplete$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          ItemActions.loadItemsSuccess,
          ItemActions.loadItemsFailure,
          ItemActions.loadFilteredItemsSuccess,
          ItemActions.loadFilteredItemsFailure,
        ),
        map(() => this.loaderService.setLoadingComplete(LoaderActionTypeEnum.LoadItems)),
      ),
    { dispatch: false },
  )

  loadFilteredItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ItemActions.loadFilteredItems),
      withLatestFrom(this.auctionFacade.auctionUrlName$, this.itemFacade.filteredItems$),
      tap(() => this.loaderService.setLoading(LoaderActionTypeEnum.LoadItems)),
      switchMap(([action, urlName, filteredItems]) => {
        const queryString = ApiUtils.buildQueryString(action.filters)
        const offset: number = filteredItems ? filteredItems.length : 0
        return this.itemService.getItems(urlName, offset, queryString)
      }),
      mergeMap((response: ItemsResponse) => [
        ItemActions.loadFilteredItemsSuccess({
          items: response.results,
        }),
        ItemActions.updateCount({
          count: response.count,
        }),
      ]),
      catchError((error) => of(ItemActions.loadFilteredItemsFailure({ error }))),
    ),
  )

  syncItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ItemActions.syncItem),
      withLatestFrom(this.auctionFacade.auctionUrlName$, this.selectedItemFacade.selectedItem$, this.itemFacade.order$),
      mergeMap(([action, urlName, selectedItem, order]) => {
        return this.itemService.syncItem(urlName, action.uuid).pipe(
          switchMap((item) => {
            const actions: Action[] = [ItemActions.syncItemSuccess({ item })]

            if (action.onSyncSuccess) {
              actions.push(action.onSyncSuccess(item))
            }

            // update selected item on pusher item.sync if uuid matches
            if (selectedItem && selectedItem.uuid === item.uuid) {
              actions.push(
                SelectedItemActions.syncSelectedItemSuccess({
                  selectedItem: item,
                }),
              )
            }

            // item ordering has changed
            if (item.order_hash !== order) {
              actions.push(
                ItemActions.updateOnItemsOrderChange({
                  order: item.order_hash,
                }),
                // reload first page of items
                ItemActions.loadItems(),
              )
            }
            return actions
          }),
          catchError((error) => of(ItemActions.resyncItemError({ error, uuid: action.uuid }))),
        )
      }),
    ),
  )

  syncItemSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ItemActions.syncItemSuccess),
      withLatestFrom(this.itemFacade.items$, this.favouriteItemsFacade.allFavouriteItemIds$),
      switchMap(([action, items, favouriteItemIds]) => {
        const actions: Action[] = [
          // add an action to update the category.item_preview
          CategoriesActions.categoryItemPreviewUpdate({
            item: action.item,
          }),
        ]

        if (items.some((item) => item.uuid === action.item.uuid)) {
          const update = {
            id: action.item.uuid,
            changes: action.item,
          }

          actions.push(ItemActions.updateItem({ update }))

          if ([...favouriteItemIds].includes(action.item.uuid)) {
            // add an action to update item in favourites
            actions.push(FavouriteItemActions.updateFavouriteItem({ update }))
          }
        } else {
          actions.push(
            ItemActions.addItem({
              item: { ...action.item, notListLoaded: true },
            }),
          )
        }
        return actions
      }),
    ),
  )

  resyncItemError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ItemActions.resyncItemError),
      filter((action) => action.error.status === 404),
      withLatestFrom(this.selectedItemFacade.selectedItemId$),
      switchMap(([action, selectedItemId]) => {
        const actions: Action[] = [ItemActions.deleteItemIfExistsInState({ uuid: action.uuid })]
        // redirect only if the not-found item is currently being viewed
        if (selectedItemId === action.uuid) {
          actions.push(ItemActions.redirectItemNotFound())
        }
        return actions
      }),
    ),
  )

  deleteItemIfExistsInState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ItemActions.deleteItemIfExistsInState),
      withLatestFrom(this.itemFacade.items$),
      switchMap(([action, items]) => {
        const actions: Action[] = []
        if (action.uuid && items.some((item) => item.uuid === action.uuid)) {
          actions.push(ItemActions.deleteItem({ uuid: action.uuid }))
        }
        return actions
      }),
    ),
  )

  redirectItemNotFound$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ItemActions.redirectItemNotFound),
        withLatestFrom(this.auctionFacade.auctionUrlName$),
        tap(([, urlName]) =>
          this.router.navigateByUrl(`/${urlName}/${NotFoundConstants.PAGE_NOT_FOUND}`, {
            state: { errorMessage: 'ITEM_NOT_FOUND' },
          }),
        ),
      ),
    { dispatch: false },
  )

  resyncItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ItemActions.resyncItems),
      distinctUntilChanged((prev, curr) => prev.offset === curr.offset && !curr.onFocus),
      withLatestFrom(this.auctionFacade.auctionUrlName$, this.itemFacade.stalePages$, this.itemFacade.order$),
      filter(([action, , stalePages]) => stalePages.includes(action.offset)),
      mergeMap(([action, urlName, , order]) =>
        this.itemService.getItems(urlName, action.offset).pipe(
          switchMap((response: ItemsResponse) => {
            let actions: Action[] = []
            const resyncSuccessActions = [
              ItemActions.resyncItemsSuccess({
                items: response.results,
                offset: action.offset,
                count: response.count,
              }),
            ]

            // item ordering not changed
            if (response.order_hash === order) {
              actions = resyncSuccessActions
            } else {
              actions = [
                ItemActions.updateOnItemsOrderChange({
                  order: response.order_hash,
                }),
              ]
              // is first page resync
              if (action.offset === 0) {
                // update items with response
                actions = actions.concat(resyncSuccessActions)
              } else {
                // reload first page of items
                actions.push(ItemActions.loadItems())
              }
            }
            return actions
          }),
          catchError((error) => of(ItemActions.resyncItemsError({ error }))),
        ),
      ),
    ),
  )

  resyncItemsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ItemActions.resyncItemsSuccess),
      switchMap((action) => [
        ItemActions.removePageFromStale({
          page: action.offset,
        }),
        ItemActions.updateCount({
          count: action.count,
        }),
      ]),
    ),
  )

  resyncItemsFirstPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ItemActions.resyncItemsFirstPage),
      withLatestFrom(this.auctionFacade.auctionUrlName$, this.itemFacade.order$),
      mergeMap(([, urlName, order]) => {
        const offset = 0

        return this.itemService.getItems(urlName, offset).pipe(
          switchMap((response: ItemsResponse) => {
            const actions: Action[] = []

            // item ordering has changed
            if (response.order_hash !== order) {
              actions.push(
                ItemActions.updateOnItemsOrderChange({
                  order: response.order_hash,
                }),
              )
            }

            actions.push(
              ItemActions.resyncItemsSuccess({
                items: response.results,
                offset,
                count: response.count,
              }),
            )

            return actions
          }),
          catchError((error) => of(ItemActions.resyncItemsError({ error }))),
        )
      }),
    ),
  )

  updateOnItemsOrderChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ItemActions.updateOnItemsOrderChange),
      switchMap((action) => [
        // update order hash string in state
        ItemActions.updateOrderHash({
          order: action.order,
        }),
        // remove all items and stale state
        ItemActions.clearItems(),
      ]),
    ),
  )

  resyncFilteredItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ItemActions.resyncFilteredItems),
      distinctUntilChanged((prev, curr) => prev.offset === curr.offset && !curr.onFocus),
      withLatestFrom(this.auctionFacade.auctionUrlName$, this.itemFacade.staleFilteredPages$, this.itemFacade.order$),
      filter(([action, , staleFilteredPages]) => staleFilteredPages.includes(action.offset)),
      mergeMap(([action, urlName, , order]) => {
        const filtersQueryStr: string = ApiUtils.buildQueryString(action.filters)

        return this.itemService.getItems(urlName, action.offset, filtersQueryStr).pipe(
          switchMap((response: ItemsResponse) => {
            let actions: Action[] = []
            const resyncSuccessActions = [
              ItemActions.resyncFilteredItemsSuccess({
                items: response.results,
                offset: action.offset,
                count: response.count,
              }),
            ]

            // item ordering not changed
            if (response.order_hash === order) {
              actions = resyncSuccessActions
            } else {
              actions = [
                ItemActions.updateOnItemsOrderChange({
                  order: response.order_hash,
                }),
              ]

              // is first page resync
              if (action.offset === 0) {
                // update filtered items with response
                actions = actions.concat(resyncSuccessActions)
              } else {
                // reload first page of filtered items
                actions.push(
                  ItemActions.loadFilteredItems({
                    filters: action.filters,
                  }),
                )
              }
            }
            return actions
          }),
          catchError((error) => of(ItemActions.resyncFilteredItemsError({ error }))),
        )
      }),
    ),
  )

  resyncFilteredItemsFirstPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ItemActions.resyncFilteredItemsFirstPage),
      withLatestFrom(this.auctionFacade.auctionUrlName$, this.itemFacade.currentFilters$, this.itemFacade.order$),
      mergeMap(([, urlName, filters, order]) => {
        const offset = 0
        const filtersQueryStr = ApiUtils.buildQueryString(filters)

        return this.itemService.getItems(urlName, offset, filtersQueryStr).pipe(
          switchMap((response: ItemsResponse) => {
            const actions: Action[] = []

            // item ordering has changed
            if (response.order_hash !== order) {
              actions.push(
                ItemActions.updateOnItemsOrderChange({
                  order: response.order_hash,
                }),
              )
            }

            actions.push(
              ItemActions.resyncFilteredItemsSuccess({
                items: response.results,
                offset,
                count: response.count,
              }),
            )

            return actions
          }),
          catchError((error) => of(ItemActions.resyncItemsError({ error }))),
        )
      }),
    ),
  )

  resyncFilteredItemsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ItemActions.resyncFilteredItemsSuccess),
      switchMap((action) => [
        ItemActions.removeFilteredPagesFromStale({
          page: action.offset,
        }),
        ItemActions.updateCount({
          count: action.count,
        }),
      ]),
    ),
  )

  resyncItemsCurrentPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ItemActions.resyncItemsCurrentPage),
      withLatestFrom(this.itemsObserverService.getPageInView(), this.itemFacade.currentFilters$),
      map(([, offset, filters]) =>
        ObjectUtils.isEmpty(filters)
          ? ItemActions.resyncItems(offset, true)
          : ItemActions.resyncFilteredItems(offset, filters, true),
      ),
    ),
  )

  createPopper$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ItemActions.createPopper),
        map((action) => this.popperService.createPopper(action.data)),
      ),
    { dispatch: false },
  )

  navigateToItem$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ItemActions.navigateToItem),
        withLatestFrom(
          this.auctionFacade.auctionUrlName$,
          this.selectedProfileFacade.selectedProfileId$,
          this.authFacade.isVolunteer$,
        ),
        tap(([action, urlName, selectedProfileId, isVolunteer]) => {
          const extras: NavigationExtras = {}
          if (action.backPath) {
            extras.queryParams = { backPath: action.backPath }
            if (isVolunteer && selectedProfileId) {
              extras.queryParams['profile'] = selectedProfileId
            }
            if (action.from) {
              extras.queryParams['from'] = action.from
            }
            if (action.categoryId) {
              extras.queryParams['categoryId'] = action.categoryId
            }
          }
          this.router.navigate([`/${urlName}/items`, action.uuid], extras)
        }),
      ),
    { dispatch: false },
  )

  showSnackbarOnUpdatePoppers$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ItemActions.showPoppers),
        filter((payload) => !!payload.snackBarMessage),
        map((payload) => this.notificationService.success(payload.snackBarMessage!)),
      ),
    { dispatch: false },
  )

  constructor(
    private actions$: Actions,
    private itemService: ItemApiService,
    private itemFacade: ItemFacade,
    private selectedItemFacade: SelectedItemFacade,
    private selectedProfileFacade: SelectedProfileFacade,
    private authFacade: AuthFacade,
    private auctionFacade: AuctionFacade,
    private favouriteItemsFacade: FavouriteItemsFacade,
    private popperService: GbPopperService,
    private loaderService: GbLoaderService,
    private notificationService: GbNotificationService,
    private itemsObserverService: GbItemsObserverService,
    private router: Router,
  ) {}
}
